import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Form, message, Space, Typography, Select, Button, Table, Tag, Modal, Input, DatePicker, Alert, Popconfirm, Tabs, InputNumber, Radio, Checkbox, Descriptions } from 'antd';
import { ReloadOutlined, PlusOutlined, ArrowLeftOutlined, LinkOutlined, DownloadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const TournamentContext = createContext(null);
const useTournamentContext = () => {
  const ctx = useContext(TournamentContext);
  if (!ctx) throw new Error("useTournamentContext must be used within a TournamentContext provider");
  return ctx;
};

const TournamentLayout = () => /* @__PURE__ */ jsx(Outlet, {});

const STATUS_LABEL = {
  draft: { label: "B\u1EA3n nh\xE1p", color: "default" },
  open: { label: "\u0110ang m\u1EDF \u0111\u0103ng k\xFD", color: "green" },
  closed: { label: "\u0110\xE3 \u0111\xF3ng \u0111\u0103ng k\xFD", color: "orange" },
  finished: { label: "\u0110\xE3 k\u1EBFt th\xFAc", color: "blue" },
  canceled: { label: "\u0110\xE3 hu\u1EF7", color: "red" }
};
const TournamentsPage = () => {
  const { request } = useTournamentContext();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await request(`?status=${encodeURIComponent(status)}`, void 0, "GET");
      setRows(r?.data ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [request, status]);
  useEffect(() => {
    void load();
  }, [load]);
  const create = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const r = await request("", {
        name: v.name,
        sport: v.sport || "pickleball",
        location_text: v.location || "",
        starts_on: v.dates?.[0]?.format("YYYY-MM-DD") || "",
        ends_on: v.dates?.[1]?.format("YYYY-MM-DD") || "",
        refund_policy: { event_cancel_percent: 100, withdraw_percent: 100 }
      }, "POST");
      message.success("\u0110\xE3 t\u1EA1o gi\u1EA3i (b\u1EA3n nh\xE1p)");
      setOpen(false);
      form.resetFields();
      if (r?.data?.id) navigate(String(r.data.id));
      else await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: 24 }, children: [
    /* @__PURE__ */ jsxs(Space, { style: { width: "100%", justifyContent: "space-between", marginBottom: 16 }, wrap: true, children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Gi\u1EA3i \u0111\u1EA5u" }),
        /* @__PURE__ */ jsx(Typography.Text, { type: "secondary", children: "T\u1EA1o gi\u1EA3i, nh\u1EADn \u0111\u0103ng k\xFD, thu l\u1EC7 ph\xED v\xE0 xu\u1EA5t danh s\xE1ch. L\u1EC7 ph\xED gi\u1EA3i kh\xF4ng t\xEDnh v\xE0o doanh thu s\xE2n." })
      ] }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
        /* @__PURE__ */ jsx(Select, { value: status, onChange: setStatus, style: { width: 190 }, options: [{ value: "", label: "T\u1EA5t c\u1EA3 tr\u1EA1ng th\xE1i" }, ...Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v.label }))] }),
        /* @__PURE__ */ jsx(Button, { onClick: () => navigate("refunds"), children: "Ho\xE0n ph\xED ch\u1EDD x\u1EED l\xFD" }),
        /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ReloadOutlined, {}), onClick: () => void load(), loading, children: "L\xE0m m\u1EDBi" }),
        /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => setOpen(true), children: "T\u1EA1o gi\u1EA3i" })
      ] })
    ] }),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: rows,
        pagination: false,
        scroll: { x: "max-content" },
        onRow: (r) => ({ onClick: () => navigate(String(r.id)), style: { cursor: "pointer" } }),
        columns: [
          { title: "T\xEAn gi\u1EA3i", dataIndex: "name" },
          { title: "Th\u1EDDi gian", render: (_, r) => r.startsOn ? `${dayjs(r.startsOn).format("DD/MM/YYYY")}${r.endsOn && r.endsOn !== r.startsOn ? " \u2013 " + dayjs(r.endsOn).format("DD/MM/YYYY") : ""}` : "" },
          { title: "N\u1ED9i dung", render: (_, r) => r.events?.length ?? 0, align: "right" },
          { title: "C\u1EB7p \u0111\xE3 \u0111\xF3ng ph\xED", render: (_, r) => (r.events ?? []).reduce((n, e) => n + e.paid, 0), align: "right" },
          { title: "Tr\u1EA1ng th\xE1i", dataIndex: "status", render: (s) => /* @__PURE__ */ jsx(Tag, { color: STATUS_LABEL[s].color, children: STATUS_LABEL[s].label }) }
        ]
      }
    ),
    /* @__PURE__ */ jsx(Modal, { title: "T\u1EA1o gi\u1EA3i \u0111\u1EA5u", open, onCancel: () => setOpen(false), onOk: () => void create(), confirmLoading: saving, okText: "T\u1EA1o gi\u1EA3i", cancelText: "\u0110\xF3ng", destroyOnClose: true, children: /* @__PURE__ */ jsxs(Form, { form, layout: "vertical", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "name", label: "T\xEAn gi\u1EA3i", rules: [{ required: true, message: "Nh\u1EADp t\xEAn gi\u1EA3i" }], children: /* @__PURE__ */ jsx(Input, {}) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "dates", label: "Th\u1EDDi gian thi \u0111\u1EA5u", children: /* @__PURE__ */ jsx(DatePicker.RangePicker, { format: "DD/MM/YYYY" }) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "location", label: "\u0110\u1ECBa \u0111i\u1EC3m", children: /* @__PURE__ */ jsx(Input, {}) }),
      /* @__PURE__ */ jsx(Form.Item, { name: "sport", label: "M\xF4n", initialValue: "pickleball", children: /* @__PURE__ */ jsx(Select, { options: [{ value: "pickleball", label: "Pickleball" }, { value: "badminton", label: "C\u1EA7u l\xF4ng" }, { value: "football", label: "B\xF3ng \u0111\xE1" }, { value: "other", label: "Kh\xE1c" }] }) })
    ] }) })
  ] });
};

const vnd$1 = (n) => `${Math.round(n || 0).toLocaleString("vi-VN")}\u0111`;
const REG_STATUS = {
  pending_review: { label: "Ch\u1EDD duy\u1EC7t", color: "gold" },
  pending_payment: { label: "Ch\u1EDD thu ph\xED", color: "blue" },
  paid: { label: "\u0110\xE3 \u0111\xF3ng ph\xED", color: "green" },
  waitlist: { label: "Danh s\xE1ch ch\u1EDD", color: "purple" },
  rejected: { label: "T\u1EEB ch\u1ED1i", color: "red" },
  withdrawn: { label: "\u0110\xE3 r\xFAt", color: "default" },
  disqualified: { label: "B\u1ECB lo\u1EA1i", color: "red" },
  event_canceled: { label: "N\u1ED9i dung hu\u1EF7", color: "default" }
};
const KIND = { singles: "\u0110\u01A1n", doubles: "\u0110\xF4i", mixed: "\u0110\xF4i h\u1ED7n h\u1EE3p", team: "\u0110\u1ED3ng \u0111\u1ED9i" };
const GENDER = { any: "Kh\xF4ng gi\u1EDBi h\u1EA1n", male: "Nam", female: "N\u1EEF", mixed: "Nam n\u1EEF" };
const SKILL_MAX = 8;
const TournamentDetailPage = () => {
  const { request, download, publicUrl } = useTournamentContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [regs, setRegs] = useState([]);
  const [eventFilter, setEventFilter] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [infoForm] = Form.useForm();
  const [evForm] = Form.useForm();
  const [evOpen, setEvOpen] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const payRef = React.useRef("");
  const [paySaving, setPaySaving] = useState(false);
  const [payForm] = Form.useForm();
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewForm] = Form.useForm();
  const [detail, setDetail] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffForm] = Form.useForm();
  const [notFound, setNotFound] = useState(false);
  const isNotFound = (err) => err?.status === 404 || err instanceof Error && /không tìm thấy|not found/i.test(err.message);
  const fail = (err) => {
    if (err instanceof Error) message.error(err.message);
  };
  const loadT = useCallback(async () => {
    if (!id || notFound) return;
    try {
      const r = await request(id, void 0, "GET");
      setT(r.data);
      infoForm.setFieldsValue({
        name: r.data.name,
        description: r.data.description,
        rulesText: r.data.rulesText,
        termsText: r.data.termsText,
        locationText: r.data.locationText,
        contactText: r.data.contactText,
        imageUrl: r.data.imageUrl,
        rentalCode: r.data.rentalCode,
        dates: r.data.startsOn ? [dayjs(r.data.startsOn), dayjs(r.data.endsOn || r.data.startsOn)] : void 0,
        regClosesAt: r.data.regClosesAt ? dayjs(r.data.regClosesAt) : void 0,
        drawOn: r.data.drawOn ? dayjs(r.data.drawOn) : void 0,
        bankName: r.data.bank?.bankName,
        bankBin: r.data.bank?.bankBin,
        accountNo: r.data.bank?.accountNo,
        accountName: r.data.bank?.accountName,
        eventCancelPercent: r.data.refundPolicy?.eventCancelPercent ?? 100,
        withdrawPercent: r.data.refundPolicy?.withdrawPercent ?? 100,
        withdrawUntil: r.data.refundPolicy?.withdrawUntil ? dayjs(r.data.refundPolicy.withdrawUntil) : void 0
      });
    } catch (err) {
      if (isNotFound(err)) setNotFound(true);
      else fail(err);
    }
  }, [request, id, infoForm, notFound]);
  const loadRegs = useCallback(async () => {
    if (!id || notFound) return;
    setLoading(true);
    try {
      const r = await request(`${id}/registrations?event_id=${eventFilter}&status=${encodeURIComponent(statusFilter)}`, void 0, "GET");
      setRegs(r.data ?? []);
    } catch (err) {
      if (isNotFound(err)) setNotFound(true);
      else fail(err);
    } finally {
      setLoading(false);
    }
  }, [request, id, eventFilter, statusFilter, notFound]);
  useEffect(() => {
    void loadT();
  }, [loadT]);
  useEffect(() => {
    void loadRegs();
  }, [loadRegs]);
  const saveInfo = async () => {
    const v = await infoForm.validateFields();
    try {
      await request(String(id), {
        name: v.name,
        description: v.description || "",
        rules_text: v.rulesText || "",
        terms_text: v.termsText || "",
        location_text: v.locationText || "",
        contact_text: v.contactText || "",
        image_url: v.imageUrl || "",
        rental_code: v.rentalCode || "",
        sport: t?.sport || "pickleball",
        starts_on: v.dates?.[0]?.format("YYYY-MM-DD") || "",
        ends_on: v.dates?.[1]?.format("YYYY-MM-DD") || "",
        reg_closes_at: v.regClosesAt ? v.regClosesAt.toISOString() : "",
        draw_on: v.drawOn ? v.drawOn.format("YYYY-MM-DD") : "",
        bank: { bank_name: v.bankName || "", bank_bin: v.bankBin || "", account_no: v.accountNo || "", account_name: v.accountName || "" },
        refund_policy: { event_cancel_percent: v.eventCancelPercent ?? 100, withdraw_percent: v.withdrawPercent ?? 100, withdraw_until: v.withdrawUntil ? v.withdrawUntil.format("YYYY-MM-DD") : "" }
      }, "PUT");
      message.success("\u0110\xE3 l\u01B0u th\xF4ng tin gi\u1EA3i");
      await loadT();
    } catch (err) {
      fail(err);
    }
  };
  const confirmOpen = () => {
    if (!t) return;
    const paid = (t.events ?? []).some((e) => e.status !== "canceled" && e.feeMinor > 0);
    const missing = [];
    if (paid && !(t.bank?.bankName && t.bank?.accountNo && t.bank?.accountName)) missing.push("Th\xF4ng tin ng\xE2n h\xE0ng nh\u1EADn l\u1EC7 ph\xED (ng\xE2n h\xE0ng, s\u1ED1 t\xE0i kho\u1EA3n, ch\u1EE7 t\xE0i kho\u1EA3n)");
    if (paid && !t.termsText?.trim()) missing.push("\u0110i\u1EC1u kho\u1EA3n \u0111\u0103ng k\xFD");
    if (missing.length > 0) {
      Modal.warning({ title: "Ch\u01B0a th\u1EC3 m\u1EDF \u0111\u0103ng k\xFD", content: /* @__PURE__ */ jsxs("div", { children: [
        "Gi\u1EA3i c\xF3 l\u1EC7 ph\xED n\xEAn c\u1EA7n b\u1ED5 sung tr\u01B0\u1EDBc khi m\u1EDF, n\u1EBFu kh\xF4ng V\u0110V kh\xF4ng bi\u1EBFt chuy\u1EC3n kho\u1EA3n cho ai:",
        /* @__PURE__ */ jsx("ul", { children: missing.map((m) => /* @__PURE__ */ jsx("li", { children: m }, m)) })
      ] }), okText: "\u0110\xE3 hi\u1EC3u" });
      return;
    }
    Modal.confirm({
      title: "M\u1EDF \u0111\u0103ng k\xFD?",
      content: /* @__PURE__ */ jsxs("div", { children: [
        "Trang c\xF4ng khai s\u1EBD hi\u1EC7n v\xE0 V\u0110V c\xF3 th\u1EC3 \u0111\u0103ng k\xFD ngay.",
        !t.regClosesAt && /* @__PURE__ */ jsx(Alert, { style: { marginTop: 8 }, type: "warning", showIcon: true, message: "Ch\u01B0a \u0111\u1EB7t h\u1EA1n \u0111\u0103ng k\xFD: \u0111\u0103ng k\xFD s\u1EBD m\u1EDF cho t\u1EDBi khi anh/ch\u1ECB \u0111\xF3ng th\u1EE7 c\xF4ng." }),
        !t.drawOn && !t.refundPolicy?.withdrawUntil && /* @__PURE__ */ jsx(Alert, { style: { marginTop: 8 }, type: "warning", showIcon: true, message: "Ch\u01B0a \u0111\u1EB7t ng\xE0y b\u1ED1c th\u0103m ho\u1EB7c h\u1EA1n r\xFAt: V\u0110V r\xFAt \u0111\u0103ng k\xFD s\u1EBD \u0111\u01B0\u1EE3c ho\xE0n ph\xED cho t\u1EDBi khi ban t\u1ED5 ch\u1EE9c \u0111\xF3ng gi\u1EA3i." })
      ] }),
      okText: "M\u1EDF \u0111\u0103ng k\xFD",
      cancelText: "\u0110\u1EC3 sau",
      onOk: () => setStatus("open")
    });
  };
  const setStatus = async (status) => {
    try {
      await request(`${id}/status`, { status }, "POST");
      message.success("\u0110\xE3 c\u1EADp nh\u1EADt tr\u1EA1ng th\xE1i");
      await loadT();
      await loadRegs();
    } catch (err) {
      fail(err);
    }
  };
  const saveEvent = async () => {
    const v = await evForm.validateFields();
    const cur = evOpen && evOpen !== "new" ? evOpen : null;
    try {
      await request(`${id}/events`, {
        id: cur?.id ?? 0,
        name: v.name,
        kind: v.kind,
        gender: v.gender,
        min_pairs: v.minPairs || 0,
        max_pairs: v.maxPairs || 0,
        fee_minor: v.feeMinor || 0,
        fee_per: v.feePer,
        max_events_per_player: v.maxEventsPerPlayer || 0,
        prizes: v.prizes || "",
        sort_order: cur?.sortOrder ?? 0,
        rules: {
          max_skill_each: v.maxSkillEach || 0,
          max_skill_pair: v.maxSkillPair || 0,
          tolerance_each: v.toleranceEach || 0,
          tolerance_pair: v.tolerancePair || 0,
          min_age: v.minAge || 0,
          max_age: v.maxAge || 0,
          min_total_age: v.minTotalAge || 0,
          require_dupr: !!v.requireDupr
        }
      }, "POST");
      message.success("\u0110\xE3 l\u01B0u n\u1ED9i dung");
      setEvOpen(null);
      await loadT();
    } catch (err) {
      fail(err);
    }
  };
  const openEvent = (ev) => {
    setEvOpen(ev);
    if (ev === "new") {
      evForm.resetFields();
      evForm.setFieldsValue({ kind: "doubles", gender: "any", feePer: "pair" });
      return;
    }
    evForm.setFieldsValue({ ...ev, ...ev.rules });
  };
  const act = async (path, body, ok) => {
    try {
      await request(path, body, "POST");
      message.success(ok);
      await loadT();
      await loadRegs();
      return true;
    } catch (err) {
      fail(err);
      return false;
    }
  };
  const events = t?.events ?? [];
  const evName = useMemo(() => new Map(events.map((e) => [e.id, e.name])), [events]);
  const regColumns = [
    { title: "M\xE3", dataIndex: "code" },
    { title: "N\u1ED9i dung", dataIndex: "eventId", render: (e) => evName.get(e) ?? e },
    { title: "V\u1EADn \u0111\u1ED9ng vi\xEAn", render: (_, r) => (r.participants ?? []).map((p) => `${p.fullName}${p.skillApproved ?? p.skill ? ` (${p.skillApproved ?? p.skill})` : ""}`).join(" / ") },
    { title: "Li\xEAn h\u1EC7", render: (_, r) => `${r.contactName} ${r.contactPhone}` },
    { title: "Ph\xED / \u0111\xE3 thu", render: (_, r) => `${vnd$1(r.paidMinor)} / ${vnd$1(r.feeDueMinor)}`, align: "right" },
    { title: "C\u1EDD", render: (_, r) => (r.warnings ?? []).length ? /* @__PURE__ */ jsxs(Tag, { color: "orange", children: [
      r.warnings.length,
      " c\u1EA3nh b\xE1o"
    ] }) : null },
    { title: "Tr\u1EA1ng th\xE1i", dataIndex: "status", render: (s) => /* @__PURE__ */ jsx(Tag, { color: REG_STATUS[s].color, children: REG_STATUS[s].label }) },
    { title: "", render: (_, r) => /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
      /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => setDetail(r), children: "Chi ti\u1EBFt" }),
      (r.status === "pending_review" || r.status === "waitlist") && /* @__PURE__ */ jsx(Button, { size: "small", type: "primary", onClick: () => setReviewTarget({ reg: r, action: "approve" }), children: "Duy\u1EC7t" }),
      r.status === "pending_review" && /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => setReviewTarget({ reg: r, action: "waitlist" }), children: "V\xE0o danh s\xE1ch ch\u1EDD" }),
      r.status === "pending_payment" && /* @__PURE__ */ jsx(Button, { size: "small", type: "primary", onClick: () => {
        payRef.current = `pay-${r.code}-${Date.now()}`;
        setPayTarget(r);
        payForm.setFieldsValue({ amount: r.feeDueMinor - r.paidMinor, method: "bank_transfer" });
      }, children: "Ghi thu" }),
      !["rejected", "withdrawn", "disqualified", "event_canceled"].includes(r.status) && /* @__PURE__ */ jsx(Button, { size: "small", danger: true, onClick: () => setReviewTarget({ reg: r, action: "reject" }), children: "T\u1EEB ch\u1ED1i" }),
      !["rejected", "withdrawn", "disqualified", "event_canceled"].includes(r.status) && /* @__PURE__ */ jsx(Button, { size: "small", danger: true, onClick: () => setReviewTarget({ reg: r, action: "disqualify" }), children: "Lo\u1EA1i" })
    ] }) }
  ];
  const ACTION_LABEL = { approve: "Duy\u1EC7t \u0111\u0103ng k\xFD", waitlist: "\u0110\u01B0a v\xE0o danh s\xE1ch ch\u1EDD", reject: "T\u1EEB ch\u1ED1i \u0111\u0103ng k\xFD", disqualify: "Lo\u1EA1i kh\u1ECFi gi\u1EA3i (kh\xF4ng ho\xE0n ph\xED)" };
  if (notFound) {
    return /* @__PURE__ */ jsx("div", { style: { padding: 24 }, children: /* @__PURE__ */ jsx(
      Alert,
      {
        type: "warning",
        showIcon: true,
        message: "Kh\xF4ng t\xECm th\u1EA5y gi\u1EA3i \u0111\u1EA5u",
        description: "Gi\u1EA3i kh\xF4ng t\u1ED3n t\u1EA1i ho\u1EB7c thu\u1ED9c t\u1ED5 ch\u1EE9c kh\xE1c.",
        action: /* @__PURE__ */ jsx(Button, { onClick: () => navigate("..", { relative: "path" }), children: "V\u1EC1 danh s\xE1ch gi\u1EA3i" })
      }
    ) });
  }
  return /* @__PURE__ */ jsxs("div", { style: { padding: 24 }, children: [
    /* @__PURE__ */ jsxs(Space, { style: { marginBottom: 16 }, wrap: true, children: [
      /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("..", { relative: "path" }), children: "Gi\u1EA3i \u0111\u1EA5u" }),
      /* @__PURE__ */ jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: t?.name }),
      t && /* @__PURE__ */ jsx(Tag, { color: STATUS_LABEL[t.status].color, children: STATUS_LABEL[t.status].label }),
      t && t.status !== "draft" && /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(LinkOutlined, {}), href: publicUrl(t.slug), target: "_blank", children: "Trang c\xF4ng khai" }),
      t?.status === "draft" && /* @__PURE__ */ jsx(Button, { type: "primary", onClick: confirmOpen, children: "M\u1EDF \u0111\u0103ng k\xFD" }),
      t?.status === "open" && /* @__PURE__ */ jsx(Button, { onClick: () => void setStatus("closed"), children: "\u0110\xF3ng \u0111\u0103ng k\xFD" }),
      t?.status === "closed" && /* @__PURE__ */ jsx(Button, { onClick: () => void setStatus("open"), children: "M\u1EDF l\u1EA1i \u0111\u0103ng k\xFD" }),
      t && ["open", "closed"].includes(t.status) && /* @__PURE__ */ jsx(Button, { onClick: () => void setStatus("finished"), children: "K\u1EBFt th\xFAc gi\u1EA3i" }),
      t && ["draft", "open", "closed"].includes(t.status) && /* @__PURE__ */ jsx(Popconfirm, { title: "Hu\u1EF7 c\u1EA3 gi\u1EA3i? M\u1ECDi \u0111\u0103ng k\xFD s\u1EBD b\u1ECB hu\u1EF7 v\xE0 c\xE1c kho\u1EA3n \u0111\xE3 thu sinh t\xE1c v\u1EE5 ho\xE0n.", onConfirm: () => void setStatus("canceled"), children: /* @__PURE__ */ jsx(Button, { danger: true, children: "Hu\u1EF7 gi\u1EA3i" }) })
    ] }),
    t?.status === "draft" && /* @__PURE__ */ jsx(Alert, { type: "info", showIcon: true, style: { marginBottom: 16 }, message: "Gi\u1EA3i \u0111ang l\xE0 b\u1EA3n nh\xE1p: th\xEAm n\u1ED9i dung thi \u0111\u1EA5u, nh\u1EADp th\xF4ng tin nh\u1EADn ti\u1EC1n r\u1ED3i b\u1EA5m 'M\u1EDF \u0111\u0103ng k\xFD'. Trang c\xF4ng khai ch\u1EC9 hi\u1EC7n sau khi m\u1EDF." }),
    /* @__PURE__ */ jsx(Tabs, { items: [
      { key: "info", label: "Th\xF4ng tin", children: /* @__PURE__ */ jsxs(Form, { form: infoForm, layout: "vertical", style: { maxWidth: 820 }, children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "name", label: "T\xEAn gi\u1EA3i", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
        /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "dates", label: "Th\u1EDDi gian thi \u0111\u1EA5u", children: /* @__PURE__ */ jsx(DatePicker.RangePicker, { format: "DD/MM/YYYY" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "regClosesAt", label: "H\u1EA1n \u0111\u0103ng k\xFD", children: /* @__PURE__ */ jsx(DatePicker, { showTime: true, format: "DD/MM/YYYY HH:mm" }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "drawOn", label: "Ng\xE0y b\u1ED1c th\u0103m (sau ng\xE0y n\xE0y kh\xF4ng ho\xE0n ph\xED)", children: /* @__PURE__ */ jsx(DatePicker, { format: "DD/MM/YYYY" }) })
        ] }),
        /* @__PURE__ */ jsx(Form.Item, { name: "locationText", label: "\u0110\u1ECBa \u0111i\u1EC3m", children: /* @__PURE__ */ jsx(Input, {}) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "rentalCode", label: "M\xE3 thu\xEA kh\u1ED1i s\xE2n (ticket thu\xEA s\xE2n s\u1EF1 ki\u1EC7n, tu\u1EF3 ch\u1ECDn)", children: /* @__PURE__ */ jsx(Input, {}) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "contactText", label: "Li\xEAn h\u1EC7 ban t\u1ED5 ch\u1EE9c", children: /* @__PURE__ */ jsx(Input, {}) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "imageUrl", label: "\u1EA2nh b\xECa (link)", children: /* @__PURE__ */ jsx(Input, {}) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "description", label: "Gi\u1EDBi thi\u1EC7u", children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 4 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "rulesText", label: "\u0110i\u1EC1u l\u1EC7", children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 6 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "termsText", label: "\u0110i\u1EC1u kho\u1EA3n & ho\xE0n ph\xED hi\u1EC3n th\u1ECB cho V\u0110V (c\u1EA7n ph\xE1p ch\u1EBF duy\u1EC7t)", children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 4 }) }),
        /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: "Nh\u1EADn l\u1EC7 ph\xED (chuy\u1EC3n kho\u1EA3n)" }),
        /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "bankName", label: "Ng\xE2n h\xE0ng", children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "bankBin", label: "M\xE3 BIN (VietQR)", children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "accountNo", label: "S\u1ED1 t\xE0i kho\u1EA3n", children: /* @__PURE__ */ jsx(Input, {}) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "accountName", label: "Ch\u1EE7 t\xE0i kho\u1EA3n", children: /* @__PURE__ */ jsx(Input, {}) })
        ] }),
        /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: "Ho\xE0n ph\xED" }),
        /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "eventCancelPercent", label: "BTC hu\u1EF7 n\u1ED9i dung / c\u1EB7p v\u01B0\u1EE3t tr\xECnh (%)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100 }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "withdrawPercent", label: "R\xFAt tr\u01B0\u1EDBc h\u1EA1n (%)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, max: 100 }) }),
          /* @__PURE__ */ jsx(Form.Item, { name: "withdrawUntil", label: "H\u1EA1n r\xFAt (m\u1EB7c \u0111\u1ECBnh: h\u1EA1n \u0111\u0103ng k\xFD + 2 ng\xE0y)", children: /* @__PURE__ */ jsx(DatePicker, { format: "DD/MM/YYYY" }) })
        ] }),
        /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { type: "primary", onClick: () => void saveInfo(), children: "L\u01B0u th\xF4ng tin" }) })
      ] }) },
      { key: "events", label: `N\u1ED9i dung (${events.length})`, children: /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), style: { marginBottom: 12 }, onClick: () => openEvent("new"), disabled: !t || ["finished", "canceled"].includes(t.status), children: "Th\xEAm n\u1ED9i dung" }),
        /* @__PURE__ */ jsx(Table, { rowKey: "id", pagination: false, dataSource: events, scroll: { x: "max-content" }, columns: [
          { title: "T\xEAn", dataIndex: "name" },
          { title: "Lo\u1EA1i", render: (_, e) => `${KIND[e.kind]} \xB7 ${GENDER[e.gender]}` },
          { title: "L\u1EC7 ph\xED", render: (_, e) => `${vnd$1(e.feeMinor)}/${e.feePer === "player" ? "V\u0110V" : "c\u1EB7p"}` },
          { title: "C\u1EB7p (t\u1ED1i thi\u1EC3u\u2013t\u1ED1i \u0111a)", render: (_, e) => `${e.minPairs || "\u2013"} \u2013 ${e.maxPairs || "\u221E"}` },
          { title: "\u0110\u0103ng k\xFD / \u0111\xE3 \u0111\xF3ng ph\xED", render: (_, e) => `${e.registered} / ${e.paid}` },
          { title: "Tr\u1EA1ng th\xE1i", dataIndex: "status", render: (s) => s === "canceled" ? /* @__PURE__ */ jsx(Tag, { color: "red", children: "\u0110\xE3 hu\u1EF7" }) : /* @__PURE__ */ jsx(Tag, { color: "green", children: "M\u1EDF" }) },
          { title: "", render: (_, e) => /* @__PURE__ */ jsxs(Space, { wrap: true, children: [
            /* @__PURE__ */ jsx(Button, { size: "small", onClick: () => openEvent(e), disabled: e.status === "canceled", children: "S\u1EEDa" }),
            /* @__PURE__ */ jsx(Button, { size: "small", icon: /* @__PURE__ */ jsx(DownloadOutlined, {}), onClick: () => void download(`events/${e.id}/export`, `cap-da-dong-phi-${e.id}.csv`), children: "CSV c\u1EB7p \u0111\xE3 \u0111\xF3ng ph\xED" }),
            e.status !== "canceled" && /* @__PURE__ */ jsx(Popconfirm, { title: "Hu\u1EF7 n\u1ED9i dung n\xE0y? \u0110\u0103ng k\xFD b\u1ECB hu\u1EF7, kho\u1EA3n \u0111\xE3 thu sinh t\xE1c v\u1EE5 ho\xE0n.", onConfirm: () => void act(`events/${e.id}/cancel`, { reason: "BTC hu\u1EF7 n\u1ED9i dung" }, "\u0110\xE3 hu\u1EF7 n\u1ED9i dung"), children: /* @__PURE__ */ jsx(Button, { size: "small", danger: true, children: "Hu\u1EF7 n\u1ED9i dung" }) })
          ] }) }
        ] })
      ] }) },
      { key: "regs", label: "\u0110\u0103ng k\xFD", children: /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(Space, { wrap: true, style: { marginBottom: 12 }, children: [
          /* @__PURE__ */ jsx(Select, { value: eventFilter, onChange: setEventFilter, style: { width: 220 }, options: [{ value: 0, label: "T\u1EA5t c\u1EA3 n\u1ED9i dung" }, ...events.map((e) => ({ value: e.id, label: e.name }))] }),
          /* @__PURE__ */ jsx(Select, { value: statusFilter, onChange: setStatusFilter, style: { width: 190 }, options: [{ value: "", label: "M\u1ECDi tr\u1EA1ng th\xE1i" }, ...Object.entries(REG_STATUS).map(([k, v]) => ({ value: k, label: v.label }))] }),
          /* @__PURE__ */ jsx(Button, { type: "primary", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => {
            staffForm.resetFields();
            setStaffOpen(true);
          }, children: "Nh\u1EADp \u0111\u0103ng k\xFD h\u1ED9" })
        ] }),
        /* @__PURE__ */ jsx(Table, { rowKey: "id", loading, dataSource: regs, pagination: { pageSize: 50 }, scroll: { x: "max-content" }, columns: regColumns })
      ] }) }
    ] }),
    /* @__PURE__ */ jsx(Modal, { title: evOpen === "new" ? "Th\xEAm n\u1ED9i dung thi \u0111\u1EA5u" : "S\u1EEDa n\u1ED9i dung", open: !!evOpen, onCancel: () => setEvOpen(null), onOk: () => void saveEvent(), okText: "L\u01B0u", cancelText: "\u0110\xF3ng", width: 760, destroyOnClose: true, children: /* @__PURE__ */ jsxs(Form, { form: evForm, layout: "vertical", children: [
      /* @__PURE__ */ jsx(Form.Item, { name: "name", label: "T\xEAn n\u1ED9i dung", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "\u0110\xF4i nam tr\xECnh 4.5" }) }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "kind", label: "Lo\u1EA1i", children: /* @__PURE__ */ jsx(Select, { style: { width: 150 }, options: Object.entries(KIND).map(([k, v]) => ({ value: k, label: v })) }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "gender", label: "Gi\u1EDBi t\xEDnh", children: /* @__PURE__ */ jsx(Select, { style: { width: 170 }, options: Object.entries(GENDER).map(([k, v]) => ({ value: k, label: v })) }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "minPairs", label: "C\u1EB7p t\u1ED1i thi\u1EC3u \u0111\u1EC3 m\u1EDF", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "maxPairs", label: "C\u1EB7p t\u1ED1i \u0111a (0 = kh\xF4ng gi\u1EDBi h\u1EA1n)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) })
      ] }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "feeMinor", label: "L\u1EC7 ph\xED (\u0111)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, style: { width: 150 } }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "feePer", label: "Thu theo", children: /* @__PURE__ */ jsx(Radio.Group, { options: [{ value: "pair", label: "C\u1EB7p" }, { value: "player", label: "V\u1EADn \u0111\u1ED9ng vi\xEAn" }] }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "maxEventsPerPlayer", label: "S\u1ED1 n\u1ED9i dung t\u1ED1i \u0111a m\u1ED7i V\u0110V (0 = kh\xF4ng gi\u1EDBi h\u1EA1n)", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) })
      ] }),
      /* @__PURE__ */ jsx(Typography.Title, { level: 5, children: "\u0110i\u1EC1u ki\u1EC7n (\u0111\u1EC3 tr\u1ED1ng/0 = kh\xF4ng gi\u1EDBi h\u1EA1n)" }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "maxSkillEach", label: "Tr\xECnh t\u1ED1i \u0111a m\u1ED7i ng\u01B0\u1EDDi", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 0.1 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "toleranceEach", label: "Dung sai m\u1ED7i ng\u01B0\u1EDDi", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 0.05 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "maxSkillPair", label: "T\u1ED5ng tr\xECnh t\u1ED1i \u0111a c\u1EE7a c\u1EB7p", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 0.1 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "tolerancePair", label: "Dung sai c\u1EA3 c\u1EB7p", children: /* @__PURE__ */ jsx(InputNumber, { min: 0, step: 0.05 }) })
      ] }),
      /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
        /* @__PURE__ */ jsx(Form.Item, { name: "minAge", label: "Tu\u1ED5i t\u1ED1i thi\u1EC3u", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "maxAge", label: "Tu\u1ED5i t\u1ED1i \u0111a", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "minTotalAge", label: "T\u1ED5ng tu\u1ED5i t\u1ED1i thi\u1EC3u c\u1EE7a c\u1EB7p", children: /* @__PURE__ */ jsx(InputNumber, { min: 0 }) }),
        /* @__PURE__ */ jsx(Form.Item, { name: "requireDupr", valuePropName: "checked", label: " ", children: /* @__PURE__ */ jsx(Checkbox, { children: "B\u1EAFt bu\u1ED9c m\xE3 DUPR" }) })
      ] }),
      /* @__PURE__ */ jsx(Form.Item, { name: "prizes", label: "Gi\u1EA3i th\u01B0\u1EDFng (hi\u1EC3n th\u1ECB)", children: /* @__PURE__ */ jsx(Input.TextArea, { rows: 2 }) })
    ] }) }),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        title: "Ghi thu l\u1EC7 ph\xED",
        open: !!payTarget,
        onCancel: () => setPayTarget(null),
        okText: "X\xE1c nh\u1EADn \u0111\xE3 thu",
        cancelText: "\u0110\xF3ng",
        confirmLoading: paySaving,
        destroyOnClose: true,
        onOk: async () => {
          const v = await payForm.validateFields();
          if (!payTarget || paySaving) return;
          setPaySaving(true);
          try {
            if (await act(`registrations/${payTarget.code}/payment`, { amount_minor: v.amount, method: v.method, receipt_url: v.receipt || "", note: v.note || "", client_ref: payRef.current }, "\u0110\xE3 ghi nh\u1EADn l\u1EC7 ph\xED")) setPayTarget(null);
          } finally {
            setPaySaving(false);
          }
        },
        children: [
          payTarget && (payTarget.receipts ?? []).length > 0 && /* @__PURE__ */ jsx(Alert, { style: { marginBottom: 12 }, type: "info", showIcon: true, message: "Ng\u01B0\u1EDDi \u0111\u0103ng k\xFD \u0111\xE3 g\u1EEDi bi\xEAn lai", description: (payTarget.receipts ?? []).map((u) => /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx("a", { href: u, target: "_blank", rel: "noreferrer", children: u }) }, u)) }),
          /* @__PURE__ */ jsxs(Form, { form: payForm, layout: "vertical", children: [
            /* @__PURE__ */ jsx(Form.Item, { name: "amount", label: "S\u1ED1 ti\u1EC1n (\u0111)", rules: [{ required: true }], children: /* @__PURE__ */ jsx(InputNumber, { min: 1, style: { width: 200 } }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "method", label: "H\xECnh th\u1EE9c", children: /* @__PURE__ */ jsx(Radio.Group, { options: [{ value: "bank_transfer", label: "Chuy\u1EC3n kho\u1EA3n" }, { value: "cash", label: "Ti\u1EC1n m\u1EB7t" }] }) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "receipt", label: "Link \u1EA3nh bi\xEAn lai (tu\u1EF3 ch\u1ECDn)", children: /* @__PURE__ */ jsx(Input, {}) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "note", label: "Ghi ch\xFA", children: /* @__PURE__ */ jsx(Input, {}) })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      Modal,
      {
        title: reviewTarget ? ACTION_LABEL[reviewTarget.action] : "",
        open: !!reviewTarget,
        onCancel: () => setReviewTarget(null),
        okText: "X\xE1c nh\u1EADn",
        cancelText: "\u0110\xF3ng",
        destroyOnClose: true,
        onOk: async () => {
          const v = await reviewForm.validateFields();
          if (!reviewTarget) return;
          if (await act(`registrations/${reviewTarget.reg.code}/review`, { action: reviewTarget.action, reason: v.reason || "", refund: !!v.refund }, "\u0110\xE3 c\u1EADp nh\u1EADt \u0111\u0103ng k\xFD")) {
            setReviewTarget(null);
            reviewForm.resetFields();
          }
        },
        children: [
          reviewTarget && (reviewTarget.reg.warnings ?? []).length > 0 && /* @__PURE__ */ jsx(Alert, { style: { marginBottom: 12 }, type: "warning", showIcon: true, message: "C\u1EA3nh b\xE1o c\u1EA7n Ban ch\u1EA5m tr\xECnh x\xE9t", description: reviewTarget.reg.warnings.join("; ") }),
          /* @__PURE__ */ jsxs(Form, { form: reviewForm, layout: "vertical", children: [
            /* @__PURE__ */ jsx(Form.Item, { name: "reason", label: "L\xFD do / ghi ch\xFA", children: /* @__PURE__ */ jsx(Input, {}) }),
            reviewTarget?.action === "reject" && reviewTarget.reg.paidMinor > 0 && /* @__PURE__ */ jsx(Form.Item, { name: "refund", valuePropName: "checked", children: /* @__PURE__ */ jsx(Checkbox, { children: "Ho\xE0n ph\xED \u0111\xE3 thu theo ch\xEDnh s\xE1ch (c\u1EB7p v\u01B0\u1EE3t tr\xECnh tr\u01B0\u1EDBc b\u1ED1c th\u0103m)" }) })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx(Modal, { title: detail ? `\u0110\u0103ng k\xFD ${detail.code}` : "", open: !!detail, onCancel: () => setDetail(null), footer: null, width: 760, destroyOnClose: true, children: detail && /* @__PURE__ */ jsxs(Space, { direction: "vertical", style: { width: "100%" }, children: [
      /* @__PURE__ */ jsxs(Descriptions, { size: "small", column: 2, bordered: true, children: [
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "N\u1ED9i dung", children: evName.get(detail.eventId) }),
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "Tr\u1EA1ng th\xE1i", children: /* @__PURE__ */ jsx(Tag, { color: REG_STATUS[detail.status].color, children: REG_STATUS[detail.status].label }) }),
        /* @__PURE__ */ jsxs(Descriptions.Item, { label: "Li\xEAn h\u1EC7", children: [
          detail.contactName,
          " ",
          detail.contactPhone,
          " ",
          detail.contactEmail
        ] }),
        /* @__PURE__ */ jsx(Descriptions.Item, { label: "Ngu\u1ED3n", children: detail.source === "staff" ? "Nh\xE2n vi\xEAn nh\u1EADp h\u1ED9" : "T\u1EF1 \u0111\u0103ng k\xFD" }),
        /* @__PURE__ */ jsxs(Descriptions.Item, { label: "Ph\xED", children: [
          vnd$1(detail.paidMinor),
          " / ",
          vnd$1(detail.feeDueMinor)
        ] })
      ] }),
      (detail.warnings ?? []).length > 0 && /* @__PURE__ */ jsx(Alert, { type: "warning", showIcon: true, message: detail.warnings.join("; ") }),
      /* @__PURE__ */ jsx(Table, { size: "small", rowKey: "id", pagination: false, dataSource: detail.participants ?? [], columns: [
        { title: "H\u1ECD t\xEAn", dataIndex: "fullName" },
        { title: "S\u0110T", dataIndex: "phone" },
        { title: "Ng\xE0y sinh", dataIndex: "birthDate", render: (v) => v ? dayjs(v).format("DD/MM/YYYY") : "" },
        { title: "Tr\xECnh t\u1EF1 khai", dataIndex: "skill" },
        { title: "DUPR", dataIndex: "duprId" },
        { title: "CLB", dataIndex: "club" },
        { title: "Tr\xECnh duy\u1EC7t", render: (_, p) => /* @__PURE__ */ jsx(
          InputNumber,
          {
            size: "small",
            step: 0.5,
            defaultValue: p.skillApproved,
            placeholder: "Ch\u1EA5m",
            onBlur: (e) => {
              const n = parseFloat(e.target.value);
              if (Number.isNaN(n)) return;
              if (n < 0 || n > SKILL_MAX || Math.abs(n * 20 - Math.round(n * 20)) > 1e-9) {
                message.error(`Tr\xECnh ph\u1EA3i trong kho\u1EA3ng 0\u2013${SKILL_MAX}, b\u01B0\u1EDBc 0,05 (v\xED d\u1EE5 3,5).`);
                return;
              }
              void act("participants/" + p.id + "/skill", { skill: n }, "\u0110\xE3 c\u1EADp nh\u1EADt tr\xECnh");
            }
          }
        ) }
      ] }),
      (detail.receipts ?? []).length > 0 && /* @__PURE__ */ jsxs("div", { children: [
        "Bi\xEAn lai: ",
        (detail.receipts ?? []).map((u) => /* @__PURE__ */ jsx("a", { href: u, target: "_blank", rel: "noreferrer", style: { marginRight: 8 }, children: "xem" }, u))
      ] }),
      /* @__PURE__ */ jsx(Table, { size: "small", rowKey: "id", pagination: false, dataSource: detail.payments ?? [], columns: [
        { title: "Kho\u1EA3n", dataIndex: "kind", render: (k) => k === "payment" ? "Thu" : "Ho\xE0n" },
        { title: "S\u1ED1 ti\u1EC1n", dataIndex: "amountMinor", render: vnd$1, align: "right" },
        { title: "H\xECnh th\u1EE9c", dataIndex: "method" },
        { title: "Tr\u1EA1ng th\xE1i", dataIndex: "state" },
        { title: "Ng\u01B0\u1EDDi x\xE1c nh\u1EADn", dataIndex: "confirmedBy" }
      ] }),
      ["pending_review", "pending_payment", "paid", "waitlist"].includes(detail.status) && /* @__PURE__ */ jsx(Popconfirm, { title: "R\xFAt \u0111\u0103ng k\xFD n\xE0y? Ho\xE0n ph\xED (n\u1EBFu \u0111\xE3 \u0111\xF3ng) theo ch\xEDnh s\xE1ch c\u1EE7a gi\u1EA3i.", onConfirm: async () => {
        if (await act(`registrations/${detail.code}/withdraw`, {}, "\u0110\xE3 r\xFAt \u0111\u0103ng k\xFD")) setDetail(null);
      }, children: /* @__PURE__ */ jsx(Button, { danger: true, children: "R\xFAt \u0111\u0103ng k\xFD" }) })
    ] }) }),
    /* @__PURE__ */ jsx(
      Modal,
      {
        title: "Nh\u1EADp \u0111\u0103ng k\xFD h\u1ED9",
        open: staffOpen,
        onCancel: () => setStaffOpen(false),
        okText: "\u0110\u0103ng k\xFD",
        cancelText: "\u0110\xF3ng",
        width: 760,
        destroyOnClose: true,
        onOk: async () => {
          const v = await staffForm.validateFields();
          const body = { registration: {
            event_id: v.eventId,
            contact_name: v.contactName || "",
            contact_phone: v.contactPhone,
            contact_email: v.contactEmail || "",
            consent_terms: true,
            participants: (v.players ?? []).map((p) => ({ full_name: p.fullName, phone: p.phone || "", gender: p.gender || "", birth_date: p.birthDate ? p.birthDate.format("YYYY-MM-DD") : "", skill: p.skill ?? null, dupr_id: p.duprId || "", club: p.club || "" }))
          } };
          if (await act(`${id}/registrations`, body, "\u0110\xE3 t\u1EA1o \u0111\u0103ng k\xFD")) setStaffOpen(false);
        },
        children: /* @__PURE__ */ jsxs(Form, { form: staffForm, layout: "vertical", initialValues: { players: [{}, {}] }, children: [
          /* @__PURE__ */ jsx(Form.Item, { name: "eventId", label: "N\u1ED9i dung", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Select, { options: events.filter((e) => e.status === "open").map((e) => ({ value: e.id, label: e.name })) }) }),
          /* @__PURE__ */ jsxs(Space, { wrap: true, align: "start", children: [
            /* @__PURE__ */ jsx(Form.Item, { name: "contactName", label: "Ng\u01B0\u1EDDi li\xEAn h\u1EC7", children: /* @__PURE__ */ jsx(Input, {}) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "contactPhone", label: "S\u0110T li\xEAn h\u1EC7", rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, {}) }),
            /* @__PURE__ */ jsx(Form.Item, { name: "contactEmail", label: "Email", children: /* @__PURE__ */ jsx(Input, {}) })
          ] }),
          /* @__PURE__ */ jsx(Form.List, { name: "players", children: (fields, { add, remove }) => /* @__PURE__ */ jsxs(Fragment, { children: [
            fields.map((f) => /* @__PURE__ */ jsxs(Space, { align: "baseline", wrap: true, children: [
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "fullName"], rules: [{ required: true }], children: /* @__PURE__ */ jsx(Input, { placeholder: "H\u1ECD t\xEAn" }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "phone"], children: /* @__PURE__ */ jsx(Input, { placeholder: "S\u0110T", style: { width: 130 } }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "gender"], children: /* @__PURE__ */ jsx(Select, { placeholder: "Gi\u1EDBi", style: { width: 90 }, allowClear: true, options: [{ value: "male", label: "Nam" }, { value: "female", label: "N\u1EEF" }] }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "birthDate"], children: /* @__PURE__ */ jsx(DatePicker, { placeholder: "Ng\xE0y sinh", format: "DD/MM/YYYY" }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "skill"], children: /* @__PURE__ */ jsx(InputNumber, { placeholder: "Tr\xECnh", min: 0, max: SKILL_MAX, step: 0.05 }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "duprId"], children: /* @__PURE__ */ jsx(Input, { placeholder: "DUPR", style: { width: 100 } }) }),
              /* @__PURE__ */ jsx(Form.Item, { name: [f.name, "club"], children: /* @__PURE__ */ jsx(Input, { placeholder: "CLB", style: { width: 120 } }) }),
              fields.length > 1 && /* @__PURE__ */ jsx(MinusCircleOutlined, { onClick: () => remove(f.name) })
            ] }, f.key)),
            /* @__PURE__ */ jsx(Button, { type: "dashed", icon: /* @__PURE__ */ jsx(PlusOutlined, {}), onClick: () => add({}), children: "Th\xEAm v\u1EADn \u0111\u1ED9ng vi\xEAn" })
          ] }) })
        ] })
      }
    )
  ] });
};

const vnd = (n) => `${Math.round(n || 0).toLocaleString("vi-VN")}\u0111`;
const TournamentRefundsPage = () => {
  const { request } = useTournamentContext();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [form] = Form.useForm();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await request("refunds?tournament_id=0", void 0, "GET");
      setRows(r?.data ?? []);
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  useEffect(() => {
    void load();
  }, [load]);
  const done = async () => {
    if (!target) return;
    const v = await form.validateFields();
    try {
      await request(`refunds/${target.id}/done`, { receipt_url: v.receipt || "" }, "POST");
      message.success("\u0110\xE3 ghi nh\u1EADn ho\xE0n ph\xED");
      setTarget(null);
      form.resetFields();
      await load();
    } catch (err) {
      if (err instanceof Error) message.error(err.message);
    }
  };
  return /* @__PURE__ */ jsxs("div", { style: { padding: 24 }, children: [
    /* @__PURE__ */ jsxs(Space, { style: { marginBottom: 16 }, children: [
      /* @__PURE__ */ jsx(Button, { icon: /* @__PURE__ */ jsx(ArrowLeftOutlined, {}), onClick: () => navigate("..", { relative: "path" }), children: "Gi\u1EA3i \u0111\u1EA5u" }),
      /* @__PURE__ */ jsx(Typography.Title, { level: 3, style: { margin: 0 }, children: "Ho\xE0n ph\xED ch\u1EDD x\u1EED l\xFD" })
    ] }),
    /* @__PURE__ */ jsx(
      Table,
      {
        rowKey: "id",
        loading,
        dataSource: rows,
        pagination: false,
        scroll: { x: "max-content" },
        locale: { emptyText: "Kh\xF4ng c\xF3 kho\u1EA3n ho\xE0n n\xE0o \u0111ang ch\u1EDD" },
        columns: [
          { title: "Gi\u1EA3i", dataIndex: "tournament" },
          { title: "N\u1ED9i dung", dataIndex: "eventName" },
          { title: "M\xE3 \u0111\u0103ng k\xFD", dataIndex: "code" },
          { title: "Li\xEAn h\u1EC7", render: (_, r) => `${r.contactName} ${r.contactPhone}` },
          { title: "S\u1ED1 ti\u1EC1n ho\xE0n", dataIndex: "amountMinor", align: "right", render: vnd },
          { title: "Ghi ch\xFA", dataIndex: "note" },
          { title: "", render: (_, r) => /* @__PURE__ */ jsx(Button, { type: "primary", onClick: () => setTarget(r), children: "\u0110\xE3 ho\xE0n" }) }
        ]
      }
    ),
    /* @__PURE__ */ jsxs(Modal, { title: "X\xE1c nh\u1EADn \u0111\xE3 ho\xE0n ph\xED", open: !!target, onCancel: () => setTarget(null), onOk: () => void done(), okText: "Ghi nh\u1EADn", cancelText: "\u0110\xF3ng", destroyOnClose: true, children: [
      /* @__PURE__ */ jsx(Typography.Paragraph, { children: target ? `Ho\xE0n ${vnd(target.amountMinor)} cho ${target.contactName} (${target.code}).` : "" }),
      /* @__PURE__ */ jsx(Form, { form, layout: "vertical", children: /* @__PURE__ */ jsx(Form.Item, { name: "receipt", label: "Link \u1EA3nh bi\xEAn lai ho\xE0n (tu\u1EF3 ch\u1ECDn)", children: /* @__PURE__ */ jsx(Input, {}) }) })
    ] })
  ] });
};

export { TournamentContext, TournamentDetailPage, TournamentLayout, TournamentRefundsPage, TournamentsPage, useTournamentContext };
//# sourceMappingURL=index.esm.js.map
